import { buildCommand } from "@stricli/core";

import { createApiClient, ENV_API_URL_VAR, resolveApiBaseUrl } from "../api-client.js";
import type { AppContext } from "../context.js";
import { applyEdit, decodeEditableBody, editorFromEnv } from "../edit.js";
import { NotLoggedInError } from "../errors.js";
import { forwardToLocalhost, isDelivered, parseForwardTarget } from "../forward.js";
import {
  announceRequestOrg,
  announceActiveProfile,
  globalFlags,
  resolveEffectiveOrg,
  resolveGlobals,
  resolveRequestProfile,
  type GlobalFlags,
} from "../global-flags.js";
import { bindAuth } from "../oauth/auth-binding.js";
import { colorize } from "../output/color.js";
import { CAPABILITY_EXIT, EXIT } from "../output/exit-codes.js";
import { renderJson } from "../output/format.js";

// `wbhk replay <eventId> --forward <localhost-url>` — re-deliver a captured event to a local dev
// server. The CLI fetches the event's captured headers + exact body, POSTs them to localhost
// (signature-preserving: exact bytes + the original webhook-* headers), and on a local 2xx records
// the delivery_attempt server-side (events.replay, idempotent). A non-2xx or an unreachable target
// exits non-zero and records nothing. `--edit` opens the body in $EDITOR first (to tweak a payload);
// the original signature then no longer matches the edited body (we can't re-sign — it's the sender's
// secret), so we forward it with a warning rather than silently ship something the handler will reject.

interface ReplayFlags extends GlobalFlags {
  forward?: string;
  destination?: string;
  edit: boolean;
}

export const replayCommand = buildCommand<ReplayFlags, [string], AppContext>({
  async func(this: AppContext, flags, eventId) {
    // Replay is an authed API-binding command → the org selector applies (a bad `--org`/`WBHK_ORG` errors
    // here, never silently targets the wrong org); a plain replay falls back to profile-only resolution.
    const { profile, org: selectorOrg } = await resolveRequestProfile(this, flags);
    announceActiveProfile(this, profile);
    const cred = await this.store.get(profile);
    if (cred === null) return new NotLoggedInError();
    announceRequestOrg(this, await resolveEffectiveOrg(this, profile, selectorOrg));
    const usingDestination = flags.destination !== undefined;
    const usingForward = flags.forward !== undefined;
    if (usingDestination && usingForward) {
      this.process.stderr.write(
        "use --destination OR --forward, not both — they pick different delivery targets.\n",
      );
      this.process.exitCode = EXIT.USAGE;
      return;
    }
    if (!usingDestination && !usingForward) {
      this.process.stderr.write(
        "replay needs a target: --forward <localhost-url> (the CLI delivers to your machine) or " +
          "--destination <id> (the server delivers to a registered destination).\n",
      );
      this.process.exitCode = EXIT.USAGE;
      return;
    }
    if (usingDestination && flags.edit) {
      this.process.stderr.write(
        "--edit isn't supported with --destination (the server delivers the stored bytes verbatim).\n",
      );
      this.process.exitCode = EXIT.USAGE;
      return;
    }
    if (usingForward) parseForwardTarget(flags.forward!); // throws InvalidForwardUrlError (usage)

    const apiBaseUrl = resolveApiBaseUrl({
      flag: flags.apiUrl,
      env: this.process.env?.[ENV_API_URL_VAR],
      stored: await this.store.getApiBaseUrl(profile),
    });
    const { bearer, refreshAuth } = await bindAuth({
      cred,
      profile,
      store: this.store,
      fetch: this.io.fetch,
      env: this.process.env,
    });
    const client = createApiClient({
      baseUrl: apiBaseUrl,
      apiKey: bearer,
      fetch: this.io.fetch,
      refreshAuth,
    });
    const { format, color } = resolveGlobals(this, flags);

    // --destination: the SERVER delivers to a pre-registered destination (ADR-0081). It resolves the
    // destination + the event under RLS, runs the connect-time SSRF guard, POSTs the stored bytes, and
    // records the real HTTP outcome. A one-shot replay mints a fresh idempotency key per invocation.
    if (usingDestination) {
      const attempt = await client.eventsReplay({
        eventId,
        target: { kind: "destination", destinationId: flags.destination! },
        idempotencyKey: crypto.randomUUID(),
      });
      if (format === "json") {
        this.process.stdout.write(`${renderJson(attempt)}\n`);
      } else {
        const word =
          attempt.status === "delivered"
            ? colorize("delivered", "green", color)
            : colorize(attempt.status, "red", color);
        const code = attempt.statusCode === null ? "" : ` · ${attempt.statusCode}`;
        this.process.stdout.write(
          `${word} event ${eventId} → destination ${flags.destination}${code} · recorded ${attempt.id}\n`,
        );
      }
      // a non-delivered outcome (failed / blocked) exits non-zero so a script can detect it.
      if (attempt.status !== "delivered") this.process.exitCode = EXIT.UNEXPECTED;
      return;
    }

    // --forward: the CLI delivers to localhost (the api can't reach the user's machine), then records.
    // Captured headers (signature fidelity) + exact body bytes. A bad/cross-org id surfaces as the
    // api's NOT_FOUND (mapped to the exit code) before we ever touch localhost.
    const event = await client.eventsGet(eventId);
    const { body } = await client.eventsGetPayload(eventId);

    // --edit: open the captured body in $EDITOR before forwarding, so you can tweak a payload to exercise
    // a local handler. The captured headers (incl. the ORIGINAL provider signature) still pass through
    // verbatim — we can't recompute that signature (it's the third-party sender's secret, never ours) — so
    // an edited body won't verify; we warn rather than silently ship a payload the handler will reject.
    let forwardBody = body;
    if (flags.edit) {
      const editor = editorFromEnv(this.process.env ?? {});
      if (editor === undefined) {
        this.process.stderr.write("--edit needs an editor — set $VISUAL or $EDITOR.\n");
        this.process.exitCode = EXIT.USAGE;
        return;
      }
      const text = decodeEditableBody(body);
      if (text === null) {
        this.process.stderr.write(
          "--edit only supports a text payload — this event's body isn't valid UTF-8.\n",
        );
        this.process.exitCode = EXIT.USAGE;
        return;
      }
      // A save that differs only by a trailing newline (the editor's doing) is NOT a real edit → forward
      // the original bytes exactly, like a plain replay. Only a genuine change re-encodes + warns.
      const { text: finalText, changed } = applyEdit(text, await this.io.editText(text, editor));
      if (changed) {
        forwardBody = new TextEncoder().encode(finalText);
        this.process.stderr.write(
          "note: the payload was edited — the original webhook signature no longer matches it, so a " +
            "local server that verifies signatures will reject it.\n",
        );
      }
    }

    const forwardUrl = flags.forward!; // guaranteed by the usingForward branch above
    const outcome = await forwardToLocalhost(
      { fetch: this.io.fetch, now: () => Date.now() },
      { targetUrl: forwardUrl, headers: event.headers, body: forwardBody },
    );

    if (!outcome.ok) {
      this.process.stderr.write(`could not reach ${forwardUrl}: ${outcome.reason}\n`);
      this.process.exitCode = CAPABILITY_EXIT.TARGET_UNREACHABLE;
      return;
    }
    if (!isDelivered(outcome)) {
      this.process.stderr.write(
        `${forwardUrl} returned ${outcome.status} — recording skipped (replay records only a local 2xx).\n`,
      );
      this.process.exitCode = EXIT.UNEXPECTED;
      return;
    }

    // Delivered: record the attempt server-side. A fresh session + idempotency key — a one-shot replay,
    // so re-running the command is a deliberately new attempt.
    const attempt = await client.eventsReplay({
      eventId,
      target: { kind: "localhost-tunnel", sessionId: crypto.randomUUID() },
      idempotencyKey: crypto.randomUUID(),
    });

    if (format === "json") {
      this.process.stdout.write(
        `${renderJson({ delivered: true, status: outcome.status, latencyMs: outcome.latencyMs, attempt })}\n`,
      );
    } else {
      const ok = colorize("delivered", "green", color);
      this.process.stdout.write(
        `${ok} event ${eventId} → ${forwardUrl} · ${outcome.status} · ${outcome.latencyMs}ms · recorded ${attempt.id}\n`,
      );
    }
  },
  parameters: {
    positional: {
      kind: "tuple",
      parameters: [{ parse: (v: string) => v, brief: "the event id", placeholder: "eventId" }],
    },
    flags: {
      ...globalFlags,
      forward: {
        kind: "parsed",
        parse: (v: string) => v,
        brief: "local URL to deliver to, e.g. http://localhost:3000/webhooks (the CLI delivers)",
        optional: true,
      },
      destination: {
        kind: "parsed",
        parse: (v: string) => v,
        brief:
          "a registered replay-destination id — the SERVER delivers to it (mutually exclusive with --forward)",
        optional: true,
      },
      edit: {
        kind: "boolean",
        brief:
          "open the payload in $EDITOR before forwarding (--forward only; the original signature won't re-verify)",
        default: false,
      },
    },
  },
  docs: {
    brief:
      "replay a captured event — to your localhost (--forward) or to a registered destination (--destination)",
  },
});
